import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, List,
    ListItem, ListItemIcon, ListItemText, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Trans, useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { useToast } from '../../context/ToastContext';
import { IClosingStatus, IPeriodClose, IPeriodFigures } from '../../interfaces/accounting.interface';
import { formatMoney } from './money';
import { dayLabel, monthLabel, reopenedNote } from './closingLabels';
import YearEndClose from './YearEndClose';
import { useAccountingInstitution } from './useAccountingInstitution';

const Figures: React.FC<{ figures: IPeriodFigures }> = ({ figures }) => {
    const { t } = useTranslation('accounting');
    return (
        <Grid container spacing={2}>
            {([
                [t('figures.income'), figures.totalIncome],
                [t('figures.expenses'), figures.totalExpense],
                [figures.netProfit >= 0 ? t('figures.profit') : t('figures.loss'), figures.netProfit],
                [t('figures.cashAndBankAtMonthEnd'), figures.cashAndBank],
            ] as [string, number][]).map(([label, value]) => (
                <Grid key={label} size={{ xs: 6, md: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                            <Typography variant="h6">{formatMoney(value)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

/**
 * Month-end close: locks the books through a month so nothing more can be dated in it. Months close
 * in order once they have ended and their payroll is in the books. Only an InstitutionAdmin can
 * reopen, and only the latest closed month.
 */
const MonthEndClose: React.FC = () => {
    const institutionId = useAccountingInstitution();
    const { user } = useAuth();
    const isAdmin = user?.roles.includes(UserType.InstitutionAdmin) ?? false;
    const { t } = useTranslation(['accounting', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [status, setStatus] = useState<IClosingStatus | null>(null);
    const [confirmClose, setConfirmClose] = useState(false);
    const [confirmCatchUp, setConfirmCatchUp] = useState(false);
    const [reopening, setReopening] = useState<IPeriodClose | null>(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    // Bumped after a month is closed or reopened, so the year-end section re-reads its check.
    const [monthsVersion, setMonthsVersion] = useState(0);

    const load = useCallback(async () => {
        if (!institutionId) return;
        try {
            setStatus(await AccountingService.closingStatus(institutionId));
        } catch (error) {
            showApiError(error, t('monthEnd.loadFailed'));
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const close = async () => {
        if (!status?.nextYear || !status.nextMonth) return;
        setBusy(true);
        try {
            await AccountingService.closeMonth(institutionId, status.nextYear, status.nextMonth);
            showSuccess(t('closing.isClosed', { label: monthLabel(status.nextYear, status.nextMonth) }));
            setConfirmClose(false);
            setMonthsVersion(v => v + 1);
            await load();
        } catch (error) {
            showApiError(error, t('monthEnd.closeFailed'));
            setConfirmClose(false);
            await load(); // a check may have changed since the page loaded
        } finally {
            setBusy(false);
        }
    };

    const catchUp = async () => {
        if (!status?.closeThroughYear || !status.closeThroughMonth) return;
        setBusy(true);
        try {
            await AccountingService.closeThrough(institutionId, status.closeThroughYear, status.closeThroughMonth);
            showSuccess(t('monthEnd.closedThroughToast', { month: monthLabel(status.closeThroughYear, status.closeThroughMonth) }));
            setMonthsVersion(v => v + 1);
        } catch (error) {
            showApiError(error, t('monthEnd.closeManyFailed'));
        } finally {
            setConfirmCatchUp(false);
            await load(); // on failure too: a check may have changed since the page loaded
            setBusy(false);
        }
    };

    const reopen = async () => {
        if (!reopening) return;
        setBusy(true);
        try {
            await AccountingService.reopenMonth(institutionId, reopening.year, reopening.month, reason.trim());
            showSuccess(t('closing.isOpenAgain', { label: monthLabel(reopening.year, reopening.month) }));
            setReopening(null);
            setReason('');
            setMonthsVersion(v => v + 1);
            await load();
        } catch (error) {
            showApiError(error, t('monthEnd.reopenFailed'));
        } finally {
            setBusy(false);
        }
    };

    if (!status) return <Box sx={{ p: 3 }}><Typography>{t('common:status.loading')}</Typography></Box>;

    const next = status.nextYear && status.nextMonth ? monthLabel(status.nextYear, status.nextMonth) : null;
    const latestClosed = status.history.find(h => h.status === 'CLOSED');
    const through = status.closeThroughYear && status.closeThroughMonth
        ? monthLabel(status.closeThroughYear, status.closeThroughMonth) : null;
    const monthsBehind = through && status.nextYear && status.nextMonth
        ? (status.closeThroughYear! - status.nextYear) * 12 + (status.closeThroughMonth! - status.nextMonth) + 1 : 0;

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>{t('monthEnd.title')}</Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
                {status.closedThrough
                    ? t('monthEnd.closedThrough', { date: dayLabel(status.closedThrough) })
                    : t('monthEnd.noneClosed')}
            </Alert>

            {next ? (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>{next}</Typography>
                    <List dense>
                        {status.checks.map(c => (
                            <ListItem key={c.key} disableGutters>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    {c.ok
                                        ? <CheckCircleIcon color="success" fontSize="small" titleAccess={t('closing.done')} />
                                        : <CancelIcon color="error" fontSize="small" titleAccess={t('closing.notDone')} />}
                                </ListItemIcon>
                                <ListItemText primary={c.label} secondary={c.ok ? null : c.detail} />
                            </ListItem>
                        ))}
                    </List>
                    {status.checks.some(c => !c.ok && (c.key === 'PAYROLL_POSTED' || c.key === 'PAYMENTS_POSTED')) && (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <Trans t={t} i18nKey="monthEnd.retryPayroll"
                                   components={{ link: <Link component={RouterLink} to="/accounting" /> }} />
                        </Typography>
                    )}
                    {status.preview && <Box sx={{ mb: 2 }}><Figures figures={status.preview} /></Box>}
                    <Button variant="contained" disabled={!status.canClose} onClick={() => setConfirmClose(true)}>
                        {t('closing.closeLabel', { label: next })}
                    </Button>
                    {through && (
                        <Button sx={{ ml: 1 }} onClick={() => setConfirmCatchUp(true)}>
                            {t('monthEnd.closeMonthsThrough', { count: monthsBehind, through })}
                        </Button>
                    )}
                </Paper>
            ) : (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {t('closing.nothingToClose')}
                </Alert>
            )}

            <Typography variant="h6" sx={{ mb: 1 }}>{t('monthEnd.history')}</Typography>
            {status.history.length === 0 ? (
                <Typography color="text.secondary">{t('monthEnd.noHistory')}</Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('common:fields.month')}</TableCell>
                                <TableCell>{t('common:fields.status')}</TableCell>
                                <TableCell align="right">{t('figures.income')}</TableCell>
                                <TableCell align="right">{t('figures.expenses')}</TableCell>
                                <TableCell align="right">{t('figures.profitLoss')}</TableCell>
                                <TableCell align="right">{t('overview.cashAndBank')}</TableCell>
                                <TableCell>{t('closing.closedColumn')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.history.map(h => (
                                <TableRow key={h.id}>
                                    <TableCell>{monthLabel(h.year, h.month)}</TableCell>
                                    <TableCell>
                                        {h.status === 'CLOSED'
                                            ? <Chip size="small" color="success" label={t('closing.closedChip')} />
                                            : <Chip size="small" label={t('closing.reopenedChip')} title={h.reopenReason ?? undefined} />}
                                        {h.status === 'REOPENED' && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {reopenedNote(h)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{formatMoney(h.figures.totalIncome)}</TableCell>
                                    <TableCell align="right">{formatMoney(h.figures.totalExpense)}</TableCell>
                                    <TableCell align="right">{formatMoney(h.figures.netProfit)}</TableCell>
                                    <TableCell align="right">{formatMoney(h.figures.cashAndBank)}</TableCell>
                                    <TableCell>
                                        {h.closedBy
                                            ? t('closing.dateBy', { date: new Date(h.closedAt).toLocaleDateString(), name: h.closedBy })
                                            : new Date(h.closedAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        {isAdmin && h.id === latestClosed?.id && (status.reopenBlockedReason
                                            ? <Typography variant="caption" color="text.secondary">{t('monthEnd.reopenYearFirst')}</Typography>
                                            : <Button size="small" onClick={() => setReopening(h)}>{t('closing.reopen')}</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            <YearEndClose institutionId={institutionId} isAdmin={isAdmin} refreshKey={monthsVersion} onChanged={load} />

            <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
                <DialogTitle>{t('closing.closeConfirmTitle', { label: next ?? '' })}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('monthEnd.confirmClose', { month: next ?? '' })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmClose(false)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" onClick={close} disabled={busy}>{t('monthEnd.closeMonth')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmCatchUp} onClose={() => setConfirmCatchUp(false)}>
                <DialogTitle>{t('monthEnd.closeMonthsTitle', { count: monthsBehind })}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('monthEnd.confirmCatchUp', { from: next ?? '', through: through ?? '' })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmCatchUp(false)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" onClick={catchUp} disabled={busy}>{t('monthEnd.closeMonths', { count: monthsBehind })}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={reopening !== null} onClose={() => setReopening(null)} fullWidth maxWidth="sm">
                <DialogTitle>{t('closing.reopenLabel', { label: reopening ? monthLabel(reopening.year, reopening.month) : '' })}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        {t('monthEnd.reopenIntro')}
                    </Typography>
                    <TextField id="reopen-reason" label={t('common:fields.reason')} fullWidth multiline minRows={2} value={reason}
                               inputProps={{ maxLength: 500 }} onChange={e => setReason(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReopening(null)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" color="warning" onClick={reopen} disabled={busy || !reason.trim()}>
                        {t('closing.reopenLabel', { label: reopening ? monthLabel(reopening.year, reopening.month) : '' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MonthEndClose;
